import React, { useMemo, useState } from "react";
import {
  FaMapMarkerAlt,
  FaCheckCircle,
  FaRoute,
  FaCheck,
  FaCompass,
  FaPaperPlane,
  FaSearch,
} from "react-icons/fa";
import "./scoutingMapList.scss";
import {
  useAddDestinationMutation,
  useGetDestinationsQuery,
} from "../features/api/destinationApiSlice";
import FuelLoading from "./FuelLoading";
import SEO from "../components/seo/SEO";
import PageHeader from "../components/PageHeader/PageHeader";
import { useToast } from "../components/toast/ToastContext";

export default function ScoutingMapList() {
  const { showToast } = useToast();

  // Public Form Fields States
  const [townName, setTownName] = useState("");
  const [stateName, setStateName] = useState("");
  const [suggestedBy, setSuggestedBy] = useState("");
  const [userNotes, setUserNotes] = useState("");

  // 🟢 NEW State: Tracks live text search queries inside listing columns to shrink big datasets
  const [columnSearchQuery, setColumnSearchQuery] = useState("");

  // Data Pipeline Hooks Channels
  const {
    data: destinations = [],
    isLoading,
    isError,
  } = useGetDestinationsQuery();
  const [addDestination, { isLoading: isSubmitting }] =
    useAddDestinationMutation();

  // Group and compute metrics smoothly in local cache memory arrays
  const processedRoutes = useMemo(() => {
    const groups = destinations.reduce((acc, item) => {
      const route = item.routeGroup || "Community Submissions";

      // 🟢 THE SHIELD: Complete lock. If the item is unapproved, do not add it to the rendering stack
      if (route === "Pending Approval") return acc;

      if (!acc[route]) acc[route] = [];
      acc[route].push(item);
      return acc;
    }, {});

    return Object.entries(groups).map(([routeName, items]) => {
      const total = items.length;

      // Filter arrays down by live search keyword parameters
      const searchFilteredItems = items.filter((item) => {
        if (!columnSearchQuery.trim()) return true;
        const query = columnSearchQuery.toLowerCase();
        return (
          item.townName?.toLowerCase().includes(query) ||
          item.stateName?.toLowerCase().includes(query) ||
          item.suggestedBy?.toLowerCase().includes(query)
        );
      });

      const visitedItems = searchFilteredItems.filter((i) => i.isVisited);
      const plannedItems = searchFilteredItems.filter((i) => !i.isVisited);

      const absoluteVisited = items.filter((i) => i.isVisited).length;
      const completionPercentage =
        total > 0 ? Math.round((absoluteVisited / total) * 100) : 0;

      return {
        routeName,
        total,
        visitedItems,
        plannedItems,
        completionPercentage,
      };
    });
  }, [destinations, columnSearchQuery]);

  const handlePublicSubmit = async (e) => {
    e.preventDefault();
    if (!townName.trim() || !stateName.trim()) {
      showToast("Please enter both a Town Name and State.", "error");
      return;
    }

    try {
      const response = await addDestination({
        townName: townName.trim(),
        stateName: stateName.trim(),
        routeGroup: "Community Submissions",
        platformSource: "Website",
        suggestedBy: suggestedBy.trim() || "Anonymous Follower",
        adminNotes: userNotes.trim() ? `User note: ${userNotes.trim()}` : "",
      }).unwrap();

      showToast(
        response.message || "Recommendation transmitted successfully!",
        "success"
      );
      setTownName("");
      setStateName("");
      setSuggestedBy("");
      setUserNotes("");
    } catch (err) {
      const serverMessage =
        err.data?.message || err.error || "Network link latency detected.";
      showToast(`Submission aborted: ${serverMessage}`, "error");
    }
  };

  if (isLoading)
    return (
      <div className="scouting-map-loading">
        <FuelLoading text="Opening follower scouting maps registry..." />
      </div>
    );
  if (isError)
    return (
      <div className="scouting-map-error">
        ⚠️ Failed to sync traveler route logs. Please refresh your browser
        coordinates.
      </div>
    );

  return (
    <main className="scouting-map-page fuel-fade-in">
      <SEO
        title="Community Scouting Map"
        description="Explore follower recommended towns, backroads destinations, and track what routes Mini Miles has crossed off the map live."
        slug="/community-map"
      />

      <div className="scouting-map-wrapper">
        <PageHeader
          kicker="Audience Roadmap"
          title="Community Scouting Deck"
          subtitle="You point the direction, we drive the miles. Tracking your town recommendations across the country."
        />

        {/* PUBLIC RECOMMENDATION SUBMISSION CARD BOX */}
        <section className="public-submission-card">
          <header className="form-header-row">
            <div className="icon-badge">
              <FaCompass />
            </div>
            <div>
              <h3>Put a Town on Our Map</h3>
              <p>
                Know a quiet backroad, a historic main street, or an awesome
                small-town diner? Drop the coordinates below!
              </p>
            </div>
          </header>

          <form onSubmit={handlePublicSubmit} className="public-scout-form">
            <div className="form-fields-grid">
              <div className="input-box">
                <label htmlFor="user-town">Town / City Name *</label>
                <input
                  id="user-town"
                  type="text"
                  value={townName}
                  onChange={(e) => setTownName(e.target.value)}
                  placeholder="e.g., Olin, Galena"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="input-box">
                <label htmlFor="user-state">State *</label>
                <input
                  id="user-state"
                  type="text"
                  value={stateName}
                  onChange={(e) => setStateName(e.target.value)}
                  placeholder="e.g., Iowa, Illinois"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="input-box">
                <label htmlFor="user-handle">Your Name / Social Handle</label>
                <input
                  id="user-handle"
                  type="text"
                  value={suggestedBy}
                  onChange={(e) => setSuggestedBy(e.target.value)}
                  placeholder="e.g., @john_drive (or leave blank)"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              <label htmlFor="user-notes">
                Why should we visit? What should we see? (Optional)
              </label>
              <textarea
                id="user-notes"
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                placeholder="e.g., Cool old gravel loops, amazing river overlooks, historic brick facades..."
                maxLength={250}
                disabled={isSubmitting}
              />
            </div>

            <button
              type="submit"
              className="fuel-primary-btn submit-scout-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                "Transmitting Coordinates..."
              ) : (
                <>
                  <FaPaperPlane style={{ marginRight: "8px" }} /> Send Route
                  Recommendation
                </>
              )}
            </button>
          </form>
        </section>

        {/* 🟢 NEW: FIXED SEARCH FILTER TOOLBAR BAR (Sits over lists decks dynamically) */}
        {destinations.length > 0 && (
          <div className="scouting-global-filter-toolbar">
            <div className="search-input-wrapper">
              <FaSearch className="search-mirror-icon" />
              <input
                type="text"
                value={columnSearchQuery}
                onChange={(e) => setColumnSearchQuery(e.target.value)}
                placeholder="Filter towns listing stack dynamically by town, state, or username..."
              />
              {columnSearchQuery && (
                <button
                  type="button"
                  className="btn-clear-txt"
                  onClick={() => setColumnSearchQuery("")}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}

        {/* DISPLAY GRID TRACKS */}
        {processedRoutes.length === 0 ? (
          <div className="empty-map-slate">
            <p>
              No community suggestions logged down the road wire yet. Send a
              message to get your town on our tracking board! 🗺️
            </p>
          </div>
        ) : (
          <div className="routes-segments-stack">
            {processedRoutes.map((route) => (
              <section key={route.routeName} className="route-segment-card">
                <header className="route-segment-card__header">
                  <div className="route-header-text">
                    <h2>
                      <FaRoute className="header-route-icon" /> Route Segment:{" "}
                      {route.routeName}
                    </h2>
                    <p>Total logged: {route.total} locations</p>
                  </div>

                  <div className="route-progress-gauge">
                    <div className="progress-bar-rail">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${route.completionPercentage}%` }}
                      />
                    </div>
                    <span className="percentage-text">
                      {route.completionPercentage}% Clear
                    </span>
                  </div>
                </header>

                <div className="route-segment-card__split-grid">
                  {/* PLANNED TOWNS COLUMN CONTAINER */}
                  <div className="destination-column column--planned">
                    <h3>
                      📌 Upcoming Scouting Destinations (
                      {route.plannedItems.length})
                    </h3>
                    <div className="scrolling-towns-matrix-container">
                      {" "}
                      {/* 🟢 scroll wrap window wrapper */}
                      {route.plannedItems.length === 0 ? (
                        <p className="no-items-placeholder">
                          No matching unvisited locations on this segment track.
                        </p>
                      ) : (
                        <div className="towns-list">
                          {route.plannedItems.map((town) => (
                            <div key={town._id} className="town-node-item">
                              <div className="town-node-item__top">
                                <span className="marker-pin">
                                  <FaMapMarkerAlt />
                                </span>
                                <h4>
                                  {town.townName}, {town.stateName}
                                </h4>
                              </div>
                              <span className="suggested-by-tag">
                                Recommended by {town.suggestedBy}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* VISITED TOWNS COLUMN CONTAINER */}
                  <div className="destination-column column--visited">
                    <h3>
                      <FaCheckCircle className="title-icon-check" /> Explored &
                      Completed ({route.visitedItems.length})
                    </h3>
                    <div className="scrolling-towns-matrix-container">
                      {" "}
                      {/* 🟢 scroll wrap window wrapper */}
                      {route.visitedItems.length === 0 ? (
                        <p className="no-items-placeholder">
                          No matching visited milestones found under query
                          rules.
                        </p>
                      ) : (
                        <div className="towns-list">
                          {route.visitedItems.map((town) => (
                            <div
                              key={town._id}
                              className="town-node-item town-node-item--cleared"
                            >
                              <div className="town-node-item__top">
                                <span className="check-badge-icon">
                                  <FaCheck />
                                </span>
                                <h4>
                                  {town.townName}, {town.stateName}
                                </h4>
                              </div>
                              {town.visitDate && (
                                <time className="visit-milestone-time">
                                  Visited:{" "}
                                  {new Date(town.visitDate).toLocaleDateString(
                                    "en-US",
                                    { month: "short", year: "numeric" }
                                  )}
                                </time>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
